import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export const useDraftQueue = (draftId: string | null, teamId: string | null) => {
  const [queuedPlayerIds, setQueuedPlayerIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch queue from DB
  const fetchQueue = useCallback(async () => {
    if (!draftId || !teamId) return;
    setIsLoading(true);
    try {
      const { data } = await supabase
        .from('draft_queues')
        .select('player_id, priority')
        .eq('draft_id', draftId)
        .eq('team_id', teamId)
        .order('priority', { ascending: true });

      setQueuedPlayerIds((data || []).map(d => d.player_id));
    } catch (err) {
      console.error('Failed to fetch draft queue:', err);
    } finally {
      setIsLoading(false);
    }
  }, [draftId, teamId]);

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  const addToQueue = useCallback(async (playerId: string) => {
    if (!draftId || !teamId) return;
    // Optimistic update
    setQueuedPlayerIds(prev => {
      if (prev.includes(playerId)) return prev;
      return [...prev, playerId];
    });

    const nextPriority = queuedPlayerIds.length;
    const { error } = await supabase
      .from('draft_queues')
      .insert({
        draft_id: draftId,
        team_id: teamId,
        player_id: playerId,
        priority: nextPriority,
      });

    if (error) {
      console.error('Failed to add to queue:', error);
      // Revert on error
      setQueuedPlayerIds(prev => prev.filter(id => id !== playerId));
    }
  }, [draftId, teamId, queuedPlayerIds.length]);

  const removeFromQueue = useCallback(async (playerId: string) => {
    if (!draftId || !teamId) return;
    // Optimistic update
    setQueuedPlayerIds(prev => prev.filter(id => id !== playerId));

    const { error } = await supabase
      .from('draft_queues')
      .delete()
      .eq('draft_id', draftId)
      .eq('team_id', teamId)
      .eq('player_id', playerId);

    if (error) {
      console.error('Failed to remove from queue:', error);
      fetchQueue(); // Revert by refetching
    }
  }, [draftId, teamId, fetchQueue]);

  // Remove drafted players from queue automatically
  const removeDraftedFromQueue = useCallback((draftedIds: string[]) => {
    setQueuedPlayerIds(prev => prev.filter(id => !draftedIds.includes(id)));
  }, []);

  return {
    queuedPlayerIds,
    isLoading,
    addToQueue,
    removeFromQueue,
    removeDraftedFromQueue,
    refetch: fetchQueue,
  };
};
